import { ChevronDown as ExpandMore, ChevronUp as ExpandLess, X as Close } from "lucide-react";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import PriceSlider from "./PriceSlider";

const FilterPanel = ({
  categories,
  selectedCategories,
  handleCategoryChange,
  maxPrice,
  selectedPriceRange,
  handlePriceChange,
  sortingOrder,
  toggleSortingOrder,
  handleClearFilters,
  setFilterVisible,
  filteredProducts,
}) => {
  const [priceCollapsed, setPriceCollapsed] = useState(false);
  const user = useSelector((state) => state?.auth?.data);
  const [selectedAttributes, setSelectedAttributes] = useState({});

  const handlePriceCollapse = () => setPriceCollapsed(!priceCollapsed);

  // Extract optional attributes from filteredProducts
  const optionalAttributes = (filteredProducts || []).flatMap(product => product.optionalAttributes || []);

  // Group the attributes
  const groupedAttributes = optionalAttributes.reduce((acc, { name, values }) => {
    const normalizedName = name.toLowerCase();
    const processedValues = values.map(v => typeof v === "string" ? v.toLowerCase() : v.value.toLowerCase());
    const existingAttribute = acc.find((attr) => attr.name.toLowerCase() === normalizedName);

    if (existingAttribute) {
      existingAttribute.values = [
        ...new Set([...existingAttribute.values, ...processedValues]),
      ];
    } else {
      acc.push({ name, values: processedValues });
    }

    return acc;
  }, []);

  const handleButtonClick = (attributeName, value) => {
    const newSelectedAttributes = { ...selectedAttributes };
    if (newSelectedAttributes[attributeName] === value) {
      delete newSelectedAttributes[attributeName];
    } else {
      newSelectedAttributes[attributeName] = value;
    }
    setSelectedAttributes(newSelectedAttributes);
  };

  return (
    <div className="p-6 h-full flex flex-col relative bg-white shadow-lg rounded-lg transition-all duration-300">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <span className="ml-2 font-bold text-gray-800">
            Hello, {user?.firstName || "Please sign in"}
          </span>
        </div>
        <button
          onClick={() => setFilterVisible(false)}
          className="text-black transition-transform duration-200 hover:scale-110"
        >
          <Close />
        </button>
      </div>

      <div className="filter-body flex-grow overflow-y-auto">
        <div className="grid grid-cols-1 gap-4">

          {/* Price Filter Section */}
          <div className="w-full">
            <button
              onClick={handlePriceCollapse}
              className={`mb-2 w-full border ${priceCollapsed ? "bg-[#005f5f]" : "bg-[#008080]"} 
              text-white rounded-lg p-2 shadow transition-colors duration-300`}
            >
              Price {priceCollapsed ? <ExpandMore /> : <ExpandLess />}
            </button>
            <div className="mt-2 overflow-hidden transition-all duration-300">
              <span className="font-bold text-gray-800 mb-1 block">Price Range</span>
              <PriceSlider
                maxPrice={maxPrice}
                selectedPriceRange={selectedPriceRange}
                handlePriceChange={handlePriceChange}
                currencySymbol="$"
                className="mt-1"
              />
            </div>
          </div>

          {/* Optional Attributes Section */}
          {groupedAttributes.length > 0 && (
            <div className="w-full">
              <fieldset className="border border-gray-300 p-4 rounded-lg">
                <legend className="font-bold text-gray-800">More Options</legend>
                <div className="space-y-2">
                  {groupedAttributes.map((attribute, index) => (
                    <div key={index} className="flex flex-col">
                      <span className="font-semibold text-sm">{attribute.name}</span>
                      <div className="flex flex-wrap gap-1">
                        {attribute.values.map((value, i) => (
                          <button
                            key={i}
                            onClick={() => handleButtonClick(attribute.name, value)}
                            className={`border-2 rounded-lg px-2 py-1 text-xs font-semibold transition-colors duration-300
                      ${selectedAttributes[attribute.name] === value
                                ? "bg-[#005f5f] text-white"
                                : "bg-transparent text-black hover:bg-[#ccf2f2]"
                              }`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          {/* Clear Filters Button */}
          <button
            onClick={handleClearFilters}
            className="bg-[#008080] text-white rounded-lg py-2 shadow mt-4 w-full hover:bg-[#005f5f] transition-colors duration-200"
          >
            Clear All Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
